import { useState, type ReactElement } from 'react';

import { Loader2 } from 'lucide-react';
import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';
import { Snackbar } from 'shared/ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';

import { useGetEmailFoldersQuery, useUpdateEmailProcessingMutation } from '../services/projects.api';
import type { EmailProcessingIntegration } from '../types/integrations';
import { EmailProcessingFolderField, EmailProcessingFrequencyFields } from './EmailProcessingFields';
import {
  describeSchedule,
  getInitialCustomInterval,
  getInitialFrequency,
  getProcessingSchedule,
  isEmailProcessingDirty,
} from './emailProcessing.utils';

interface Props {
  data: EmailProcessingIntegration;
  projectId: number;
}

interface EmailProcessingSummaryProps {
  data: EmailProcessingIntegration;
  selectedFolderName: string;
}

const EmailProcessingSummary = ({ data, selectedFolderName }: EmailProcessingSummaryProps): ReactElement => (
  <span className="flex flex-col gap-1">
    <span className="flex items-center gap-3">
      <span className="font-semibold text-text-default">Procesamiento de correos</span>
      <Badge className={data.enableInboxFolder ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'}>
        {data.enableInboxFolder ? 'Habilitado' : 'Deshabilitado'}
      </Badge>
    </span>
    {data.enableInboxFolder && (
      <span className="flex items-center gap-2 text-xs font-normal text-text-subtle">
        <span className="max-w-[22ch] truncate">{selectedFolderName}</span>
        {data.processingSchedule && <span>· {describeSchedule(data.processingSchedule)}</span>}
      </span>
    )}
  </span>
);

interface SaveActionsProps {
  isLoading: boolean;
  onDiscard: () => void;
}

const SaveActions = ({ isLoading, onDiscard }: SaveActionsProps): ReactElement => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-subtle bg-bg-surface-raised px-4 py-3 shadow-save-bar">
    <span className="text-sm text-text-muted">Cambios sin guardar</span>
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="sm" disabled={isLoading} onClick={onDiscard}>
        Descartar
      </Button>
      <Button type="submit" size="sm" disabled={isLoading}>
        {isLoading && <Loader2 data-icon="inline-start" className="animate-spin" />}
        {isLoading ? 'Guardando...' : 'Guardar cambios'}
      </Button>
    </div>
  </div>
);

const EmailProcessingSection = ({ data, projectId }: Props): ReactElement => {
  const sectionProps = useCollapsibleSection('email-processing');
  const [enableInboxFolder, setEnableInboxFolder] = useState(data.enableInboxFolder);
  const [inboxFolder, setInboxFolder] = useState(data.inboxFolder);
  const [frequency, setFrequency] = useState(() => getInitialFrequency(data.processingSchedule));
  const [customInterval, setCustomInterval] = useState(() => getInitialCustomInterval(data.processingSchedule));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [updateEmailProcessing, { isLoading }] = useUpdateEmailProcessingMutation();
  const {
    data: emailFolders = [],
    isFetching: isFetchingEmailFolders,
    isError: isEmailFoldersError,
    refetch: refetchEmailFolders,
  } = useGetEmailFoldersQuery(projectId, { skip: !enableInboxFolder });

  const { scheduleEditable } = data;
  const selectedFolderName = emailFolders.find((folder) => folder.id === data.inboxFolder)?.name ?? data.inboxFolder;
  const trimmedFolder = inboxFolder.trim();
  const customIntervalNumber = Number(customInterval);
  const processingSchedule = getProcessingSchedule({
    customInterval,
    existingSchedule: data.processingSchedule,
    frequency,
  });

  const folderMissing = enableInboxFolder && !trimmedFolder;
  const scheduleMissing = enableInboxFolder && scheduleEditable && !processingSchedule;
  const isDirty = isEmailProcessingDirty({
    dataEnabled: data.enableInboxFolder,
    dataFolder: data.inboxFolder,
    dataSchedule: data.processingSchedule,
    enabled: enableInboxFolder,
    folder: trimmedFolder,
    schedule: processingSchedule,
    scheduleEditable,
  });
  const canSave = isDirty && !folderMissing && !scheduleMissing && !isLoading;

  // Required-field errors only after a failed submit, so enabling the switch doesn't flash red on empty fields.
  const [showErrors, setShowErrors] = useState(false);

  const clearError = (): void => setSaveError(null);

  const handleDiscard = (): void => {
    setEnableInboxFolder(data.enableInboxFolder);
    setInboxFolder(data.inboxFolder);
    setFrequency(getInitialFrequency(data.processingSchedule));
    setCustomInterval(getInitialCustomInterval(data.processingSchedule));
    setShowErrors(false);
    clearError();
  };

  const handleSave = async (): Promise<void> => {
    if (folderMissing || scheduleMissing) {
      setShowErrors(true);
      return;
    }
    if (!canSave) return;

    clearError();
    try {
      const response = await updateEmailProcessing({
        projectId,
        enableInboxFolder,
        ...(enableInboxFolder && {
          inboxFolder: trimmedFolder,
          ...(scheduleEditable && { processingSchedule }),
        }),
      }).unwrap();
      setInboxFolder(trimmedFolder);
      setShowErrors(false);
      setSavedMessage(response.message);
    } catch (error) {
      setSaveError(extractErrorMessage(error, 'No se pudo guardar la configuración.'));
    }
  };

  return (
    <>
      <Accordion
        type="single"
        collapsible
        {...sectionProps}
        className="bg-bg-surface border border-border-subtle rounded-xl"
      >
        <AccordionItem value="content" className="border-none">
          <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
            <EmailProcessingSummary data={data} selectedFolderName={selectedFolderName} />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
            <form
              className="flex flex-col gap-5 pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSave();
              }}
            >
              <FieldGroup>
                <Field orientation="horizontal">
                  <FieldLabel htmlFor="email-processing-enabled">Procesar correos de Gmail</FieldLabel>
                  <Switch
                    id="email-processing-enabled"
                    checked={enableInboxFolder}
                    disabled={isLoading}
                    onCheckedChange={(checked) => {
                      setEnableInboxFolder(checked);
                      clearError();
                    }}
                  />
                </Field>

                {enableInboxFolder && (
                  <>
                    <EmailProcessingFolderField
                      emailFolders={emailFolders}
                      inboxFolder={inboxFolder}
                      isEmailFoldersError={isEmailFoldersError}
                      isFetchingEmailFolders={isFetchingEmailFolders}
                      isLoading={isLoading}
                      showError={showErrors && folderMissing}
                      onChange={(folderId) => {
                        setInboxFolder(folderId);
                        clearError();
                      }}
                      onRefresh={() => void refetchEmailFolders()}
                    />
                    <EmailProcessingFrequencyFields
                      customInterval={customInterval}
                      customIntervalNumber={customIntervalNumber}
                      data={data}
                      frequency={frequency}
                      isLoading={isLoading}
                      processingSchedule={processingSchedule}
                      showError={showErrors && scheduleMissing}
                      onCustomIntervalChange={(value) => {
                        setCustomInterval(value);
                        clearError();
                      }}
                      onFrequencyChange={(value) => {
                        setFrequency(value);
                        clearError();
                      }}
                    />
                  </>
                )}
              </FieldGroup>

              {saveError && (
                <Alert variant="destructive">
                  <AlertTitle>No se pudo guardar</AlertTitle>
                  <AlertDescription className="break-words">{saveError}</AlertDescription>
                </Alert>
              )}

              {isDirty && <SaveActions isLoading={isLoading} onDiscard={handleDiscard} />}
            </form>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Snackbar open={savedMessage !== null} message={savedMessage ?? ''} onClose={() => setSavedMessage(null)} />
    </>
  );
};

export default EmailProcessingSection;
